import { memo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { VariableSizeList } from "react-window";
import { findIndex } from "lodash";
import { makeStyles } from "@mui/styles";

import { selectedDriverSelector } from "../../reducers/ownApp/selectors";
import {
  driverEditModeSelector,
  driverEditSelector,
  driversImpactedWeekCombinedSelector,
  selectingDriverLocationSelector,
} from "../../reducers/driverManagement/selectors";

import { DriverCardBase } from "./DriverCardBase";
import { View } from "../View";

const useStyles = makeStyles(({ typography }) => ({
  item: {
    marginTop: typography.pxToRem(2),
  },
  noScrollbar: {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
}));

export const DriverList = memo(
  ({
    showWorkSchedule,
    drivers,
    driverProfileLocks,
    matrixDays,
    vehicles,
    height = 900,
  }) => {
    const listRef = useRef();
    const selectedDriver = useSelector(selectedDriverSelector);
    const driverEdit = useSelector(driverEditSelector);
    const driverEditMode = useSelector(driverEditModeSelector);
    const selectingDriverLocation = useSelector(
      selectingDriverLocationSelector
    );
    const driversImpacted = useSelector(driversImpactedWeekCombinedSelector);
    const styles = useStyles();

    useEffect(() => {
      if ((!selectedDriver && !driverEdit) || !drivers) return;
      const focusDriver = driverEdit || selectedDriver;
      listRef.current.resetAfterIndex(0, true);
      let selectedIndex = findIndex(
        drivers,
        (driver) => driver.driverCode === focusDriver.driverCode
      );
      if (selectedIndex < 0) return;
      listRef.current.scrollToItem(selectedIndex, "start");
    }, [drivers, selectedDriver, driverEdit]);

    useEffect(() => {
      listRef?.current.resetAfterIndex(0, true);
    }, [drivers]);

    const renderRow = ({ index, style }) => {
      const driver = drivers[index];
      const vehicle = vehicles[driver?.vehicleType];
      const inEdit = driverEdit?.driverCode === driver.driverCode;
      const selected =
        driver.driverCode === selectedDriver?.driverCode ||
        driversImpacted[driver.driverCode];
      return (
        <View
          className={styles.item}
          key={index}
          style={style}
          id={`driver-card-${index}`}
        >
          <DriverCardBase
            showWorkSchedule={showWorkSchedule}
            height={style.height}
            driver={driver}
            vehicle={vehicle}
            driverProfileLocks={driverProfileLocks}
            matrixDays={matrixDays}
            editMode={driverEditMode}
            inEdit={inEdit}
            selectingDriverLocation={selectingDriverLocation}
            selected={selected}
          />
        </View>
      );
    };

    const getItemSize = () => (showWorkSchedule ? 190 : 120);

    return (
      <>
        {Array.isArray(drivers) && (
          <VariableSizeList
            className={styles.noScrollbar}
            ref={listRef}
            height={height}
            itemCount={drivers.length}
            itemSize={getItemSize}
            width={showWorkSchedule ? 370 : 313}
          >
            {renderRow}
          </VariableSizeList>
        )}
      </>
    );
  }
);

export default DriverList;
